import React, { useMemo, useState } from 'react';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { Table } from 'tea-component';
import { useIntl, getLocale } from 'umi';
import SortBy, {
  SortByItemType,
} from '@/pages/intelligence/components/TableComponents/components/SortBy';
import TableShowData from '@/pages/intelligence/components/TableComponents/components/TableShowData';
import type { RankTrendTableCProps } from '@intelligence/types/topchart';
import { addV2Prefix, growthFormat, newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import ARightClickToJump from '@/pages/intelligence/components/ARightClickToJump';
import defaultIcon from '@/pages/intelligence/assets/svg/flag/default.svg';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import FreeIcon from '@/pages/intelligence/assets/svg/dataSource/appstore.svg';
import VideoIcon from '@/pages/intelligence/assets/svg/dataSource/googleplay.svg';
import { getCountryFlag } from '@home/_config/countries';
import {
  DataSourceBubbleInfo,
  DATASOURCE_NAME,
} from '@/pages/intelligence/components/DataSourceComponents';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { getMarketKeyList } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/utils';
import RingRatioBubble from '@/pages/opinion/components/Snippets/RingRatioBubble';
import SvgIcon from '@home/components/SvgIcon';

const { pageable, scrollable, autotip } = Table.addons;

const TableContent = function (props: RankTrendTableCProps) {
  const {
    tableData,
    isFree,
    entityType,
    pageIndex,
    setPageIndex,
    setTableSelection,
    tableSelection,
    changeMarketProfileAll,
    selectAllKeys,
    regionsCountries,
    onSortChage,
    pageSortAttr = {},
    setIsOperateCheckbox,
    getLineChartData,
    isTableNodata = false,
    radioType,
    rankType,
    reqDateObject,
    basicInfo,
    dateType,
  } = props;
  const isAvg = rankType === 'avg_ranking';
  const bubTitle = useMemo(() => {
    let defaultStr = 'dbi_segment_rank_trend_avg';
    if (rankType !== 'avg_ranking') {
      defaultStr =
        radioType === 'dod' ? 'dbi_segment_rank_trend_dod' : 'dbi_segment_rank_trend_period';
    }
    return defaultStr;
  }, [radioType, rankType]);
  const { sortKey = '', currentSortValue = 'desc' } = pageSortAttr;
  const { formatMessage } = useIntl();
  const isEN = getLocale() === 'en-US';
  const allMarkets = regionsCountries?.map((area: any) => area.country_set).flat() || [];
  // 跳转到游戏详情
  const gotoDetailsPage = (val: any, _jump = true) => {
    const path = `/intelligence/MarketProfile/Overview/detail?marketName=${val.market}&marketNameEn=${val.marketEn}&pageId=${entityType}`;
    return path;
  };
  const changeMpTableSort = function (e: SortByItemType) {
    onSortChage(e);
    setPageIndex(1);
  };

  const [pageSize, setPageSize] = useState(50);

  // 如果有主动传值无数据，则使用空数组，否则使用原数据
  const tableDataCur = useMemo(() => {
    return isTableNodata ? [] : tableData;
  }, [isTableNodata, tableData]);

  const columns: any[] = [
    {
      key: 'select',
      width: 37,
      fixed: 'left',
      header: () => (
        <TableCheckboxHeader
          disableChckekList={[]}
          selectList={tableSelection}
          tableSelectionAll={selectAllKeys}
          onChange={(params, type) => {
            // console.log(params, 'params');
            // console.log(type, 'dddd');
            setIsOperateCheckbox(true);
            changeMarketProfileAll({ type });
          }}
        />
      ),
      render: (cvm: any) => (
        <TableCheckboxRender
          selectList={tableSelection}
          cvm={{ ...(cvm || {}), gameId: cvm?.market }}
          onChange={(list) => {
            // console.log(params,'params');
            // console.log(list, 'list');
            setIsOperateCheckbox(true);
            setTableSelection(list);
            getLineChartData(getMarketKeyList(list));
          }}
        />
      ),
    },
    {
      key: 'market',
      header: (
        <div className="dbi-marketprofile-table-title">
          <span>#</span> {formatMessage({ id: 'dbi_countries' })}
        </div>
      ),
      width: 240,
      fixed: 'left',
      render: (item: any) => {
        const href = gotoDetailsPage(item, false);
        const imgUrl = getCountryFlag(item.market);
        return (
          <ARightClickToJump href={addV2Prefix(href)} style={{ display: 'inline' }}>
            <div
              className="dbi-marketprofile-table-cont"
              onClick={() => {
                gotoDetailsPage(item);
              }}
            >
              <span className="dbi-marketprofile-table-rank">{item?.rank}</span>
              <div className="dbi-marketprofile-table-img">
                {imgUrl ? (
                  <img
                    className={'normalFlag'}
                    src={imgUrl}
                    onError={(e: any) => {
                      e.target.onerror = null;
                      e.target.src = defaultIcon;
                    }}
                  />
                ) : (
                  <></>
                )}
              </div>
              <span className={`dbi-marketprofile-table-text`}>
                {allMarkets?.find((market: any) => market.country_code === item.market)?.[
                  isEN ? 'country_name_en' : 'country_name_ch'
                ] || item.market}
              </span>
            </div>
          </ARightClickToJump>
        );
      },
    },
    {
      key: 'appstore top free',
      header: () => {
        const defaultKey = isFree ? 'appstore top free' : 'appstore top paid';
        const titleStr = isFree
          ? formatMessage({ id: `dbi_store_rank_top_free` })
          : formatMessage({ id: `dbi_store_rank_top_paid` });
        const dataSource = 'appstore';
        const showTextBubble = sortKey !== `${defaultKey}_1`;
        const contentTextId = isFree ? 'dbi_info_appstore_topfree' : 'dbi_info_appstore_top_paid';
        return (
          <CloumsCenter width={240} align={'right'}>
            <SortBy
              onChange={changeMpTableSort}
              defaultKey={sortKey}
              defaultValue={currentSortValue}
              title={titleStr}
              svgIcon={FreeIcon}
              list={[
                {
                  key: `${defaultKey}_0`,
                  sortMetric: defaultKey,
                  text: titleStr,
                  title: titleStr,
                  ratioType: 0,
                },
                {
                  key: `${defaultKey}_1`,
                  sortMetric: defaultKey,
                  text: `${titleStr}:${formatMessage({ id: 'dbi_change' })}`,
                  title: `${titleStr}:Change`,
                  ratioType: 1,
                },
              ]}
              svgTip={
                <DataSourceBubbleInfo
                  dataOfDom={titleStr}
                  metricKey={defaultKey}
                  entityType={entityType}
                  comesFormDom={
                    <>
                      <img src={getDataSourceIcon(dataSource)} />
                      {DATASOURCE_NAME[dataSource]}
                    </>
                  }
                  dataSource={dataSource}
                />
              }
              content={showTextBubble && formatMessage({ id: contentTextId })}
            />
          </CloumsCenter>
        );
      },
      align: 'center',
      width: 240,
      render: (cvm: any) => {
        const sortMetric = isFree ? 'appstore top free' : 'appstore top paid';
        const metrics = cvm?.metrics || [];
        const metric = metrics.find((item: any) => item?.metric === sortMetric);
        const value = newMathFormat(metric?.value);
        const linkValue = newMathFormat(metric?.link_value);
        const growthDataObj = growthFormat({
          value: metric?.dod,
          valueType: 'rankValue',
          hasPoint: isAvg ? 'all' : 'none',
          fixedNum: 2,
          maxNumber: 99999,
          zeroValue: '-',
        });
        return (
          <CloumsCenter width={240} align={'center'}>
            <TableShowData
              maxNumber={'99999+%'}
              value={value}
              type={growthDataObj?.type}
              growth={
                <RingRatioBubble
                  title={formatMessage({ id: bubTitle })}
                  otherInfo={
                    <div className={'dbi-table-content-right-ratio'}>
                      <div>{formatMessage({ id: 'dbi_change' })}</div>
                      <div
                        className={
                          growthDataObj?.type === 'up' ? `dbi-row-successnum` : `dbi-row-errornum`
                        }
                        style={{ width: '68px', marginRight: '-5px' }}
                      >
                        {growthDataObj?.value}
                        {growthDataObj?.value != '-' ? (
                          <SvgIcon
                            className={`dbi-row-arrow`}
                            type={growthDataObj?.type === 'up' ? 'green-up-new' : 'red-down-new'}
                          />
                        ) : null}
                      </div>
                    </div>
                  }
                  newTime={[reqDateObject?.start_date, reqDateObject?.end_date]}
                  oldTime={[reqDateObject?.link_start_date, reqDateObject?.link_end_date]}
                  oldValue={linkValue}
                  newValue={value}
                  placement={'top-start'}
                >
                  <div className={'dbi-table-content-hot-hover-area'}></div>
                  {growthDataObj?.value}
                </RingRatioBubble>
              }
            />
          </CloumsCenter>
        );
      },
    },
    {
      // todo 保留两位小数
      key: 'appstore top grossing',
      header: () => (
        <CloumsCenter width={240} align={'right'}>
          <SortBy
            onChange={changeMpTableSort}
            defaultKey={sortKey}
            defaultValue={currentSortValue}
            title={formatMessage({ id: `dbi_store_rank_top_grossing` })}
            svgIcon={FreeIcon}
            list={[
              {
                key: `appstore top grossing_0`,
                sortMetric: 'appstore top grossing',
                text: formatMessage({ id: `dbi_store_rank_top_grossing` }),
                title: formatMessage({ id: `dbi_store_rank_top_grossing` }),
                ratioType: 0,
              },
              {
                key: `appstore top grossing_1`,
                sortMetric: 'appstore top grossing',
                text: `${formatMessage({ id: `dbi_store_rank_top_grossing` })}:${formatMessage({
                  id: 'dbi_change',
                })}`,
                title: `${formatMessage({ id: `dbi_store_rank_top_grossing` })}:Change`,
                ratioType: 1,
              },
            ]}
            svgTip={
              <DataSourceBubbleInfo
                dataOfDom={formatMessage({ id: `dbi_store_rank_top_grossing` })}
                metricKey={'appstore top grossing'}
                entityType={entityType}
                comesFormDom={
                  <>
                    <img src={getDataSourceIcon('appstore')} />
                    {DATASOURCE_NAME.appstore}
                  </>
                }
                dataSource={'appstore'}
              />
            }
            content={
              sortKey !== 'appstore top grossing_1' &&
              formatMessage({ id: 'dbi_info_appstore_topgrossing' })
            }
          />
        </CloumsCenter>
      ),
      align: 'center',
      width: 240,
      render: (cvm: any) => {
        const sortMetric = 'appstore top grossing';
        const metrics = cvm?.metrics || [];
        const metric = metrics.find((item: any) => item?.metric === sortMetric);
        const value = newMathFormat(metric?.value);
        const linkValue = newMathFormat(metric?.link_value);
        const growthDataObj = growthFormat({
          value: metric?.dod,
          valueType: 'rankValue',
          hasPoint: isAvg ? 'all' : 'none',
          fixedNum: 2,
          maxNumber: 99999,
          zeroValue: '-',
        });
        return (
          <CloumsCenter width={240} align={'center'}>
            <TableShowData
              maxNumber={'99999+%'}
              value={value}
              type={growthDataObj?.type}
              growth={
                <RingRatioBubble
                  title={formatMessage({ id: bubTitle })}
                  otherInfo={
                    <div className={'dbi-table-content-right-ratio'}>
                      <div>{formatMessage({ id: 'dbi_change' })}</div>
                      <div
                        className={
                          growthDataObj?.type === 'up' ? `dbi-row-successnum` : `dbi-row-errornum`
                        }
                        style={{ width: '68px', marginRight: '-5px' }}
                      >
                        {growthDataObj?.value}
                        {growthDataObj?.value != '-' ? (
                          <SvgIcon
                            className={`dbi-row-arrow`}
                            type={growthDataObj?.type === 'up' ? 'green-up-new' : 'red-down-new'}
                          />
                        ) : null}
                      </div>
                    </div>
                  }
                  newTime={[reqDateObject?.start_date, reqDateObject?.end_date]}
                  oldTime={[reqDateObject?.link_start_date, reqDateObject?.link_end_date]}
                  oldValue={linkValue}
                  newValue={value}
                  placement={'top-start'}
                >
                  <div className={'dbi-table-content-hot-hover-area'}></div>
                  {growthDataObj?.value}
                </RingRatioBubble>
              }
            />
          </CloumsCenter>
        );
      },
    },
    {
      key: 'googleplay top free',
      header: () => {
        const defaultKey = isFree ? 'googleplay top free' : 'googleplay top paid';
        const titleStr = isFree
          ? formatMessage({ id: `dbi_store_rank_top_free` })
          : formatMessage({ id: `dbi_store_rank_top_paid` });
        const dataSource = 'googleplay';
        const showTextBubble = sortKey !== `${defaultKey}_1`;
        const contentTextId = isFree
          ? 'dbi_info_googleplay_topfree'
          : 'dbi_info_googleplay_toppaid';
        return (
          <CloumsCenter width={240} align={'right'}>
            <SortBy
              onChange={changeMpTableSort}
              defaultKey={sortKey}
              defaultValue={currentSortValue}
              title={titleStr}
              svgIcon={VideoIcon}
              list={[
                {
                  key: `${defaultKey}_0`,
                  sortMetric: defaultKey,
                  text: titleStr,
                  title: titleStr,
                  ratioType: 0,
                },
                {
                  key: `${defaultKey}_1`,
                  sortMetric: defaultKey,
                  text: `${titleStr}:${formatMessage({ id: 'dbi_change' })}`,
                  title: `${titleStr}:Change`,
                  ratioType: 1,
                },
              ]}
              svgTip={
                <DataSourceBubbleInfo
                  dataOfDom={titleStr}
                  metricKey={defaultKey}
                  entityType={entityType}
                  comesFormDom={
                    <>
                      <img src={getDataSourceIcon(dataSource)} />
                      {DATASOURCE_NAME[dataSource]}
                    </>
                  }
                  dataSource={dataSource}
                />
              }
              content={showTextBubble && formatMessage({ id: contentTextId })}
            />
          </CloumsCenter>
        );
      },
      align: 'center',
      width: 240,
      render: (cvm: any) => {
        const sortMetric = isFree ? 'googleplay top free' : 'googleplay top paid';
        const metrics = cvm?.metrics || [];
        const metric = metrics.find((item: any) => item?.metric === sortMetric);
        const value = newMathFormat(metric?.value);
        const linkValue = newMathFormat(metric?.link_value);
        const growthDataObj = growthFormat({
          value: metric?.dod,
          valueType: 'rankValue',
          hasPoint: isAvg ? 'all' : 'none',
          fixedNum: 2,
          maxNumber: 99999,
          zeroValue: '-',
        });
        return (
          <CloumsCenter width={240} align={'center'}>
            <TableShowData
              maxNumber={'99999+%'}
              value={value}
              type={growthDataObj?.type}
              growth={
                <RingRatioBubble
                  title={formatMessage({ id: bubTitle })}
                  otherInfo={
                    <div className={'dbi-table-content-right-ratio'}>
                      <div>{formatMessage({ id: 'dbi_change' })}</div>
                      <div
                        className={
                          growthDataObj?.type === 'up' ? `dbi-row-successnum` : `dbi-row-errornum`
                        }
                        style={{ width: '68px', marginRight: '-5px' }}
                      >
                        {growthDataObj?.value}
                        {growthDataObj?.value != '-' ? (
                          <SvgIcon
                            className={`dbi-row-arrow`}
                            type={growthDataObj?.type === 'up' ? 'green-up-new' : 'red-down-new'}
                          />
                        ) : null}
                      </div>
                    </div>
                  }
                  newTime={[reqDateObject?.start_date, reqDateObject?.end_date]}
                  oldTime={[reqDateObject?.link_start_date, reqDateObject?.link_end_date]}
                  oldValue={linkValue}
                  newValue={value}
                  placement={'top-start'}
                >
                  <div className={'dbi-table-content-hot-hover-area'}></div>
                  {growthDataObj?.value}
                </RingRatioBubble>
              }
            />
          </CloumsCenter>
        );
      },
    },
    {
      key: 'googleplay top grossing',
      header: () => (
        <CloumsCenter width={240} align={'right'}>
          <SortBy
            onChange={changeMpTableSort}
            defaultKey={sortKey}
            defaultValue={currentSortValue}
            title={formatMessage({ id: `dbi_store_rank_top_grossing` })}
            svgIcon={VideoIcon}
            list={[
              {
                key: `googleplay top grossing_0`,
                sortMetric: 'googleplay top grossing',
                text: formatMessage({ id: `dbi_store_rank_top_grossing` }),
                title: formatMessage({ id: `dbi_store_rank_top_grossing` }),
                ratioType: 0,
              },
              {
                key: `googleplay top grossing_1`,
                sortMetric: 'googleplay top grossing',
                text: `${formatMessage({ id: `dbi_store_rank_top_grossing` })}:${formatMessage({
                  id: 'dbi_change',
                })}`,
                title: `${formatMessage({ id: `dbi_store_rank_top_grossing` })}:Change`,
                ratioType: 1,
              },
            ]}
            svgTip={
              <DataSourceBubbleInfo
                dataOfDom={formatMessage({ id: `dbi_store_rank_top_grossing` })}
                metricKey={'googleplay top grossing'}
                entityType={entityType}
                comesFormDom={
                  <>
                    <img src={getDataSourceIcon('googleplay')} />
                    {DATASOURCE_NAME.googleplay}
                  </>
                }
                dataSource={'googleplay'}
              />
            }
            content={
              sortKey !== 'googleplay top grossing_1' &&
              formatMessage({ id: 'dbi_info_googleplay_topgrossing' })
            }
          />
        </CloumsCenter>
      ),
      align: 'center',
      width: 240,
      render: (cvm: any) => {
        const sortMetric = 'googleplay top grossing';
        const metrics = cvm?.metrics || [];
        const metric = metrics.find((item: any) => item?.metric === sortMetric);
        const value = newMathFormat(metric?.value);
        const linkValue = newMathFormat(metric?.link_value);
        const growthDataObj = growthFormat({
          value: metric?.dod,
          valueType: 'rankValue',
          hasPoint: isAvg ? 'all' : 'none',
          fixedNum: 2,
          maxNumber: 99999,
          zeroValue: '-',
        });
        return (
          <CloumsCenter width={240} align={'right'}>
            <TableShowData
              maxNumber={'99999+%'}
              value={value}
              type={growthDataObj?.type}
              growth={
                <RingRatioBubble
                  title={formatMessage({ id: bubTitle })}
                  otherInfo={
                    <div className={'dbi-table-content-right-ratio'}>
                      <div>{formatMessage({ id: 'dbi_change' })}</div>
                      <div
                        className={
                          growthDataObj?.type === 'up' ? `dbi-row-successnum` : `dbi-row-errornum`
                        }
                        style={{ width: '68px', marginRight: '-5px' }}
                      >
                        {growthDataObj?.value}
                        {growthDataObj?.value != '-' ? (
                          <SvgIcon
                            className={`dbi-row-arrow`}
                            type={growthDataObj?.type === 'up' ? 'green-up-new' : 'red-down-new'}
                          />
                        ) : null}
                      </div>
                    </div>
                  }
                  newTime={[reqDateObject?.start_date, reqDateObject?.end_date]}
                  oldTime={[reqDateObject?.link_start_date, reqDateObject?.link_end_date]}
                  oldValue={linkValue}
                  newValue={value}
                  placement={'top-start'}
                >
                  <div className={'dbi-table-content-hot-hover-area'}></div>
                  {growthDataObj?.value}
                </RingRatioBubble>
              }
            />
          </CloumsCenter>
        );
      },
    },
    {
      key: 'googleplay top popular',
      header: () => (
        <CloumsCenter width={240} align={'right'}>
          <SortBy
            onChange={changeMpTableSort}
            defaultKey={sortKey}
            defaultValue={currentSortValue}
            title={formatMessage({ id: `dbi_store_rank_top_popular` })}
            svgIcon={VideoIcon}
            list={[
              {
                key: `googleplay top popular_0`,
                sortMetric: 'googleplay top popular',
                text: formatMessage({ id: `dbi_store_rank_top_popular` }),
                title: formatMessage({ id: `dbi_store_rank_top_popular` }),
                ratioType: 0,
              },
              {
                key: `googleplay top popular_1`,
                sortMetric: 'googleplay top popular',
                text: `${formatMessage({ id: `dbi_store_rank_top_popular` })}:${formatMessage({
                  id: 'dbi_change',
                })}`,
                title: `${formatMessage({ id: `dbi_store_rank_top_popular` })}:Change`,
                ratioType: 1,
              },
            ]}
            svgTip={
              <DataSourceBubbleInfo
                dataOfDom={formatMessage({ id: `dbi_store_rank_top_popular` })}
                metricKey={'googleplay top popular'}
                entityType={entityType}
                comesFormDom={
                  <>
                    <img src={getDataSourceIcon('googleplay')} />
                    {DATASOURCE_NAME.googleplay}
                  </>
                }
                dataSource={'googleplay'}
              />
            }
            content={''}
          />
        </CloumsCenter>
      ),
      align: 'center',
      width: 240,
      render: (cvm: any) => {
        const sortMetric = 'googleplay top popular';
        const metrics = cvm?.metrics || [];
        const metric = metrics.find((item: any) => item?.metric === sortMetric);
        const value = newMathFormat(metric?.value);
        const linkValue = newMathFormat(metric?.link_value);
        const growthDataObj = growthFormat({
          value: metric?.dod,
          valueType: 'rankValue',
          hasPoint: isAvg ? 'all' : 'none',
          fixedNum: 2,
          maxNumber: 99999,
          zeroValue: '-',
        });
        return (
          <CloumsCenter width={240} align={'center'}>
            <TableShowData
              maxNumber={'99999+%'}
              value={value}
              type={growthDataObj?.type}
              growth={
                <RingRatioBubble
                  title={formatMessage({ id: bubTitle })}
                  otherInfo={
                    <div className={'dbi-table-content-right-ratio'}>
                      <div>{formatMessage({ id: 'dbi_change' })}</div>
                      <div
                        className={
                          growthDataObj?.type === 'up' ? `dbi-row-successnum` : `dbi-row-errornum`
                        }
                        style={{ width: '68px', marginRight: '-5px' }}
                      >
                        {growthDataObj?.value}
                        {growthDataObj?.value != '-' ? (
                          <SvgIcon
                            className={`dbi-row-arrow`}
                            type={growthDataObj?.type === 'up' ? 'green-up-new' : 'red-down-new'}
                          />
                        ) : null}
                      </div>
                    </div>
                  }
                  newTime={[reqDateObject?.start_date, reqDateObject?.end_date]}
                  oldTime={[reqDateObject?.link_start_date, reqDateObject?.link_end_date]}
                  oldValue={linkValue}
                  newValue={value}
                  placement={'top-start'}
                >
                  <div className={'dbi-table-content-hot-hover-area'}></div>
                  {growthDataObj?.value}
                </RingRatioBubble>
              }
            />
          </CloumsCenter>
        );
      },
    },
  ];
  const handleOnChangePageIng = (value: any) => {
    setPageSize(value.pageSize);
    setPageIndex?.(value.pageIndex);
  };
  return (
    <Table
      className={
        'tablecard-box dbi-table-selectable dbi-table-sticky dbi-user-guide__market-profile__table'
      } // 解决冲突保留的
      bordered="all"
      records={tableDataCur || []}
      columns={columns}
      addons={[
        pageable({
          pageSize,
          pageIndex,
          onPagingChange: handleOnChangePageIng,
        }),
        scrollable({
          minWidth: 400,
        }),
        autotip({
          emptyText: formatMessage({ id: 'dbi_detail_no_data_under' }),
        }),
      ]}
    />
  );
};
export default TableContent;
